import { useState, useEffect, useCallback } from 'react';
import { servicesAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import ImagePickerField from '../../components/ui/ImagePickerField';

export default function ServicesPage() {
  const [services, setServices]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPag]        = useState(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [filter, setFilter]         = useState({ category:'', status:'' });
  const [modal, setModal]           = useState(false);
  const [selected, setSel]          = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState({
    sku: '', name: '', slug: '', category_id: '',
    description: '', problem_solved: '',
    status: 'active', is_public: true, media_url: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Strip empty strings — don't send status='' or category='' to backend
      const params = { page };
      if (filter.category) params.category = filter.category;
      if (filter.status)   params.status   = filter.status;
      const res = await servicesAPI.list(params);
      setServices(res.data.data || []); setPag(res.data.pagination);
    } catch { setServices([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    servicesAPI.categories().then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setForm({ sku: `MH-SRV-NEW-${String(services.length + 1).padStart(3,'0')}`, name: '', slug: '', category_id: categories[0]?.id || '', description: '', problem_solved: '', status: 'active', is_public: true, media_url: '' });
    setSel(null); setModal(true);
  };
  const openEdit = (s) => {
    setForm({ sku: s.sku, name: s.name, slug: s.slug, category_id: s.category_id, description: s.description || '', problem_solved: s.problem_solved || '', status: s.status, is_public: s.is_public, media_url: s.media_url || '' });
    setSel(s); setModal(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      if (!selected) await servicesAPI.create(form);
      else           await servicesAPI.update(selected.id, form);
      setModal(false); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await servicesAPI.remove(id); load();
  };

  const catColors = ['var(--blue)', 'var(--accent)', 'var(--green)', 'var(--amber)', 'var(--blue)'];

  const COLS = [
    { key: 'thumbnail_url', label: '', render: (v, row) => {
      const src = v || row.media_url;
      return (
        <div style={{ width:36, height:36, borderRadius:6, overflow:'hidden', background:'var(--bg-elevated)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {src
            ? <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/>
            : <span style={{ fontSize:14, opacity:0.3, color:'var(--accent)' }}>◈</span>}
        </div>
      );
    }},
    { key:'sku', label:'SKU', render:v => <span style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent)', background:'var(--accent-muted)', padding:'2px 8px', borderRadius:4 }}>{v}</span> },
    { key:'name', label:'Service', render:(v,row) => (
      <div>
        <div style={{ fontWeight:500 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.category_name}</div>
      </div>
    )},
    { key:'status', label:'Status', render:v => <Badge status={v}/> },
    { key:'is_public', label:'Visibility', render:v => <span style={{ fontSize:12, color:v?'var(--green)':'var(--text-muted)' }}>{v?'Public':'Hidden'}</span> },
    { key:'_actions', label:'', render:(_,row) => (
      <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
        <Button variant="ghost"  size="sm" onClick={()=>openEdit(row)}>Edit</Button>
        <Button variant="danger" size="sm" onClick={()=>handleDelete(row.id)}>Delete</Button>
      </div>
    )},
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Category filter cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
        {categories.map((cat,i) => (
          <div key={cat.id}
            onClick={()=>setFilter(f=>({...f, category:f.category===cat.slug?'':cat.slug}))}
            style={{ background:filter.category===cat.slug?'var(--accent-muted)':'var(--bg-surface)', border:`1px solid ${filter.category===cat.slug?'var(--accent)':'var(--border)'}`, borderRadius:'var(--radius-md)', padding:'12px 14px', cursor:'pointer', transition:'all 0.15s' }}>
            <div style={{ fontSize:11, fontFamily:'monospace', color:catColors[i], fontWeight:700, marginBottom:4 }}>{cat.code}</div>
            <div style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:1.4 }}>{cat.name}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <Select value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{ width:160 }}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="coming_soon">Coming Soon</option>
          </Select>
          <span style={{ fontSize:12, color:'var(--text-muted)' }}>{pagination?.total??0} services</span>
        </div>
        <Button onClick={openCreate}>+ New Service</Button>
      </div>

      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        <Table columns={COLS} data={services} loading={loading} onRowClick={openEdit}/>
        <Pagination pagination={pagination} onPage={setPage}/>
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={selected?`Edit — ${selected.name}`:'New Service'} width={660}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ gridColumn:'1 / -1' }}>
            <ImagePickerField label="Cover Image" value={form.media_url} onChange={url=>setForm(f=>({...f,media_url:url}))}/>
          </div>
          <Input label="SKU" value={form.sku} onChange={e=>setForm(f=>({...f,sku:e.target.value}))} style={{ fontFamily:'monospace' }}/>
          <Select label="Category" value={form.category_id} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))}>
            {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Service Name" value={form.name} onChange={e=>{
              const slug = e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
              setForm(f=>({...f,name:e.target.value,slug}));
            }}/>
          </div>
          <Input label="URL Slug" value={form.slug} onChange={e=>setForm(f=>({...f,slug:e.target.value}))} style={{ fontFamily:'monospace' }}/>
          <Select label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="coming_soon">Coming Soon</option>
          </Select>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={3} style={taStyle}/>
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Problem It Solves" value={form.problem_solved} onChange={e=>setForm(f=>({...f,problem_solved:e.target.value}))}/>
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:8 }}>
            <input type="checkbox" checked={form.is_public} onChange={e=>setForm(f=>({...f,is_public:e.target.checked}))}/>
            <span style={{ fontSize:13, color:'var(--text-secondary)' }}>Public (visible on SPA)</span>
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:10, justifyContent:'flex-end', paddingTop:6 }}>
            <Button variant="secondary" onClick={()=>setModal(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selected?'Save Changes':'Create Service'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };
const taStyle    = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' };